import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { BiPencil, BiTrash } from 'react-icons/bi';
import { PiDotsThree } from 'react-icons/pi';
import { z } from 'zod';

import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import AddButton from '@/components/Button/AddButton';
import { Input, TextArea } from '@/components/Inputs';
import { LoaderSpinner } from '@/components/LoaderSpinner/LoaderSpinner';
import { Popup, PopupSection, type PopupSectionItem } from '@/components/Popup/Popup';
import { Tile } from '@/components/Tile/Tile';
import { UnitInput } from '@/components/UnitInput/UnitInput';
import { useClickOutside } from '@/hooks/useClickOutside';
import { noNegativeNumberProps } from '@/utils/form';
import { speedAndCourse, toDegreesMinutes } from '@/utils/position';
import { convertSpeed, round1, SPEED_UNITS, type SpeedUnit } from '@/utils/unitConversion';

import { currentLocalTime, timeMaskProps } from '../../logbookTime';
import { useEntryFix } from '../../useEntryFix';
import type { NavigationEntry } from '../types';

import s from './NavEntriesSection.module.scss';

interface NavEntriesSectionProps {
  entries: NavigationEntry[];
  editingId: string | null;
  addingNew: boolean;
  onStartAdd(): void;
  onStartEdit(id: string): void;
  onCancel(): void;
  onSaveEdit(id: string, patch: Partial<NavigationEntry>): void;
  onAddNew(values: Omit<NavigationEntry, 'id'>): void;
  onRemove(id: string): void;
}

export function NavEntriesSection({
  entries,
  editingId,
  addingNew,
  onStartAdd,
  onStartEdit,
  onCancel,
  onSaveEdit,
  onAddNew,
  onRemove,
}: NavEntriesSectionProps) {
  // A new entry is keyed in the unit the crew used last.
  const lastSpeedUnit = [...entries].reverse().find((e) => e.speedUnit)?.speedUnit;

  return (
    <Tile>
      <section className={s.section}>
        <h6 className={s.sectionTitle}>Navigation Entries</h6>

        {entries.length === 0 && !addingNew && (
          <p className={s.empty}>Please add an entry to get started.</p>
        )}

        {entries.map((entry, index) =>
          entry.id === editingId ? (
            <NavEntryEdit
              key={entry.id}
              number={index + 1}
              entry={entry}
              onCancel={onCancel}
              onSubmit={(values) => onSaveEdit(entry.id, values)}
            />
          ) : (
            <NavEntryItem
              key={entry.id}
              number={index + 1}
              entry={entry}
              onEdit={() => onStartEdit(entry.id)}
              onDelete={() => onRemove(entry.id)}
            />
          ),
        )}

        {addingNew ? (
          <NavEntryEdit
            number={entries.length + 1}
            defaultSpeedUnit={lastSpeedUnit}
            onCancel={onCancel}
            onSubmit={(values) => onAddNew(values)}
          />
        ) : (
          <AddButton label="Navigation Entry" onClick={onStartAdd} />
        )}
      </section>
    </Tile>
  );
}

interface NavEntryItemProps {
  number: number;
  entry: NavigationEntry;
  onEdit(): void;
  onDelete(): void;
}

function NavEntryItem({ number, entry, onEdit, onDelete }: NavEntryItemProps) {
  const [opened, setOpened] = useState(false);
  const ref = useClickOutside<HTMLButtonElement>(() => setOpened(false));

  const actionsList: PopupSectionItem[] = [
    { label: 'Edit', onClick: onEdit, icon: <BiPencil /> },
    { label: 'Delete', onClick: onDelete, icon: <BiTrash /> },
  ];

  return (
    <article className={s.inputsGroup}>
      <div className={s.groupTitle}>
        <p>Entry #{number}</p>
        <button type="button" className={s.actions} onClick={() => setOpened((o) => !o)} ref={ref}>
          <PiDotsThree size={20} />
          {opened && (
            <Popup>
              <PopupSection items={actionsList} title="" onItemClick={() => setOpened(false)} />
            </Popup>
          )}
        </button>
      </div>
      <section className={s.inputs}>
        <Field label="Time" value={entry.time} />
        <Field label="Position" value={entry.position} />
        <Field label="Course" value={entry.course} />
        <Field label="RPM" value={entry.rpm} />
        <Field label="Person at Helm" value={entry.personAtHelm} />
        <Field label="Speed" value={formatSpeed(entry)} />
        <Field label="Weather Conditions" value={entry.weatherConditions} />
        <Field label="Visibility (VIS)" value={entry.visibility} />
        <Field label="Wind Direction (DIR)" value={entry.windDir} />
        <Field label="Wind Force" value={entry.windForce} />
        <Field label="Comments" value={entry.comments} stretch />
      </section>
    </article>
  );
}

const formatSpeed = ({ speed, speedUnit }: NavigationEntry) =>
  speed.trim() && speedUnit ? `${speed} ${speedUnit}` : speed;

interface NavEntryEditProps {
  number: number;
  entry?: NavigationEntry;
  /** The unit a new entry's speed starts in; knots when the log has none yet. */
  defaultSpeedUnit?: SpeedUnit;
  onCancel(): void;
  onSubmit(values: Omit<NavigationEntry, 'id'>): void;
}

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const FIELD_MAX = 35;
const COMMENTS_MAX = 45;

const fieldString = (label: string) =>
  z.string().max(FIELD_MAX, `Please keep the ${label} to ${FIELD_MAX} characters or fewer.`);

const entrySchema = z.object({
  time: z.string().refine((v) => !v || TIME_RE.test(v), 'Use HH:MM format (00:00–23:59)'),
  position: fieldString('position'),
  course: fieldString('course'),
  rpm: fieldString('RPM'),
  personAtHelm: fieldString('person at helm'),
  speed: fieldString('speed').refine(
    (v) => !v.trim() || (Number.isFinite(Number(v)) && Number(v) >= 0),
    'Please enter the speed as a number.',
  ),
  speedUnit: z.enum(SPEED_UNITS as [SpeedUnit, ...SpeedUnit[]]),
  weatherConditions: fieldString('weather conditions'),
  visibility: fieldString('visibility'),
  windDir: fieldString('wind direction'),
  windForce: fieldString('wind force'),
  comments: z.string().max(COMMENTS_MAX, `Max ${COMMENTS_MAX} characters`),
});

type EntryFormValues = z.infer<typeof entrySchema>;

function NavEntryEdit({
  number,
  entry,
  defaultSpeedUnit = 'kn',
  onCancel,
  onSubmit,
}: NavEntryEditProps) {
  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    mode: 'onChange',
    defaultValues: {
      time: entry?.time ?? currentLocalTime(),
      position: entry?.position ?? '',
      course: entry?.course ?? '',
      rpm: entry?.rpm ?? '',
      personAtHelm: entry?.personAtHelm ?? '',
      speed: entry?.speed ?? '',
      speedUnit: entry?.speedUnit ?? defaultSpeedUnit,
      weatherConditions: entry?.weatherConditions ?? '',
      visibility: entry?.visibility ?? '',
      windDir: entry?.windDir ?? '',
      windForce: entry?.windForce ?? '',
      comments: entry?.comments ?? '',
    },
  });
  const speedUnit = useWatch({ control, name: 'speedUnit' });
  // A new entry starts at the boat's position, speed and course.
  const locating = useEntryFix(!entry, { getValues, setValue }, (coords) => {
    const { speedKn, course } = speedAndCourse(coords.speed, coords.course);
    return {
      // The comma matters: the mapper splits Position at it into latitude and longitude.
      position: toDegreesMinutes(coords.latitude, coords.longitude, ', '),
      course,
      // In whatever unit is picked by the time the fix comes in.
      speed:
        speedKn === undefined
          ? ''
          : String(round1(convertSpeed(speedKn, 'kn', getValues('speedUnit')))),
    };
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={s.inputsGroup}>
      <div className={s.groupTitle}>
        <p>Entry #{number}</p>
      </div>
      <section className={s.editInputs}>
        <Input
          label="Time"
          placeholder="HH:MM"
          error={errors.time?.message}
          {...timeMaskProps(register('time'))}
        />
        <Input
          label="Position"
          placeholder={locating ? 'Locating…' : '43°44.123′N, 7°25.476′E'}
          suffixNode={locating && <LoaderSpinner size={16} fullHeight={false} label="" />}
          error={errors.position?.message}
          {...register('position')}
        />
        <Input label="Course" error={errors.course?.message} {...register('course')} />
        <Input label="RPM" error={errors.rpm?.message} {...register('rpm')} />
        <Input
          label="Person at Helm"
          error={errors.personAtHelm?.message}
          {...register('personAtHelm')}
        />
        <Controller
          control={control}
          name="speed"
          render={({ field }) => (
            <UnitInput
              kind="speed"
              label="Speed"
              type="number"
              step="any"
              {...noNegativeNumberProps}
              error={errors.speed?.message}
              value={field.value ?? ''}
              // A pasted "-5" bypasses the key guard; drop the sign here too.
              onChange={(v) => field.onChange(v.replace(/-/g, ''))}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              unit={speedUnit}
              onUnitChange={(next) => setValue('speedUnit', next as SpeedUnit)}
            />
          )}
        />
        <Input
          label="Weather Conditions"
          error={errors.weatherConditions?.message}
          {...register('weatherConditions')}
        />
        <Input
          label="Visibility (VIS)"
          error={errors.visibility?.message}
          {...register('visibility')}
        />
        <Input
          label="Wind Direction (DIR)"
          error={errors.windDir?.message}
          {...register('windDir')}
        />
        <Input label="Wind Force" error={errors.windForce?.message} {...register('windForce')} />
        <TextArea
          label="Comments"
          placeholder="Wind backing northwest, one reef in the main."
          error={errors.comments?.message}
          className={s.stretch}
          {...register('comments')}
        />
      </section>
      <div className={s.groupItemActions}>
        <ButtonOutlinedSmall type="button" onClick={onCancel}>
          Cancel
        </ButtonOutlinedSmall>
        <ButtonSmall type="submit">{entry ? 'Save' : 'Add'}</ButtonSmall>
      </div>
    </form>
  );
}

interface FieldProps {
  label: string;
  value?: string;
  stretch?: boolean;
}

function Field({ label, value, stretch }: FieldProps) {
  return (
    <article className={clsx(s.field, stretch && s.stretch)}>
      <span className={`body-medium ${s.fieldLabel}`}>{label}</span>
      <div className={s.fieldValue}>{value && value.length ? value : '-'}</div>
    </article>
  );
}
